// Dispatch Orders — the Dispatch team's queue.
//
// Confirmed sales orders only. A sales order appears here automatically the
// moment its pipeline record reaches Order Confirmed (see
// src/api-sales/order-from-pipeline.js), so Dispatch never has to be told a
// sale happened.
//
// The payment and stock badges are ADVISORY. The authoritative credit-limit
// and negative-stock gates run at the moment a delivery challan is actually
// raised (src/api-accounts/dispatch-credit-check.js) — a green badge here
// means "nothing obviously blocks this", not "approved to ship".

import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { DispatchAndChallansPage } from './DispatchAndChallansPage';

interface Shortage {
  productId: string;
  description: string;
  required: number;
  onHand: number;
  short: number;
}

interface StockFlag {
  status: 'open' | 'promised' | 'resolved';
  raisedAt: string | null;
  shortages: Shortage[];
  promisedShipDate: string | null;
  resolutionNote: string;
}

/** A challan already raised against this order, from GET .../dispatch-queue. */
interface LinkedChallan {
  _id: string;
  /** The allocated DC number once dispatched; a DRAFT-xxxx placeholder before. */
  documentNumber: string;
  status: string;
  date: string;
}

interface DispatchOrder {
  grandTotal: number;
  paymentStatus: 'uninvoiced' | 'paid' | 'partial' | 'unpaid';
  _id: string;
  orderNumber: string;
  date: string;
  confirmedAt: string | null;
  status: string;
  customerName: string;
  customerPhone: string;
  shippingDestination: string;
  lineCount: number;
  stockStatus: 'ready' | 'short' | 'unknown';
  shortages: Shortage[];
  unknownLines: number;
  stockFlag: StockFlag | null;
  challans: LinkedChallan[];
  requiredDispatchDate?: string | null;
  isOverdue?: boolean;
  overdueDays?: number;
  dispatchHandoff: {
    transportMode: 'in_house_driver' | 'third_party_courier' | 'local_transport';
    driverName: string;
    courierName: string;
    note: string;
    requiredDispatchDate?: string | null;
  } | null;
}

type DispatchStatusFilter = 'all' | 'pending_challan' | 'draft_challan' | 'dispatched' | 'delivered' | 'overdue';

export function useDispatchQueue(filters: { search?: string; dispatchStatus?: DispatchStatusFilter } = {}) {
  return useQuery({
    queryKey: ['dispatch', 'queue', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search?.trim()) params.set('search', filters.search.trim());
      if (filters.dispatchStatus && filters.dispatchStatus !== 'all') params.set('dispatchStatus', filters.dispatchStatus);
      const { data } = await api.get<{ data: DispatchOrder[] }>(`/api/sales-ops/orders/dispatch-queue${params.toString() ? `?${params}` : ''}`);
      return data.data ?? [];
    },
    // Orders land here from the pipeline and from finalised invoices without a
    // page action, so poll rather than wait for a manual refresh.
    refetchInterval: 60_000,
  });
}
export function DispatchOrdersPage({ readOnly = false }: { readOnly?: boolean }) {
  return <DispatchAndChallansPage readOnly={readOnly} />;
}

export default DispatchOrdersPage;
